import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import { CungVeDichAPI } from '../services/API';
import { SaleReportSubmissionAPI, uploadImage } from '../services/feature-api';
import { useThangDoanhSo, ThongBaoKhoaThang } from '../utils/thangDoanhSo';

// AutocompleteSelect component for product search dropdown
const AutocompleteSelect = ({
  options,
  value,
  onChange,
  placeholder,
  displayField,
  searchFields = [],
  disabled = false,
  maxResults = 100
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options
    .filter(option => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return searchFields.some(field => {
        const fieldValue = option[field]?.toString().toLowerCase() || '';
        return fieldValue.includes(searchLower);
      });
    })
    .slice(0, maxResults);

  const getDisplayText = () => {
    if (!value) return '';
    const selected = options.find(opt => opt.id.toString() === value.toString());
    return selected ? displayField(selected) : '';
  };

  const handleSelect = (option) => {
    onChange(option.id.toString());
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-3 pr-8 py-2 text-base md:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Không tìm thấy kết quả</div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`px-3 py-2 cursor-pointer text-sm ${index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'text-gray-900 hover:bg-gray-50'}`}
              >
                {displayField(option)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const SaleReportForm = ({ userInfo, onLogout, token, totalPoints, accessToken, onRefreshPoints }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Full product catalog from inventory
  const [selectedProductId, setSelectedProductId] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [summaryData, setSummaryData] = useState([]); // For 2025 T1-T8 summary data
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  // Step 2 — proof submission
  const [step, setStep] = useState(1);
  const [proofImageFile, setProofImageFile] = useState(null);
  const [proofImagePreview, setProofImagePreview] = useState(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submittedSnapshot, setSubmittedSnapshot] = useState({ totalAmount: 0, details: [] });

  // Generate month options based on year
  const getMonthOptions = (year) => {
    if (year === '2025') {
      return [
        { value: 'T1-T8', label: 'T1 - T8' },
        { value: '09', label: 'Tháng 9' },
        { value: '10', label: 'Tháng 10' },
        { value: '11', label: 'Tháng 11' },
        { value: '12', label: 'Tháng 12' }
      ];
    } else {
      return [
        { value: '01', label: 'Tháng 1' },
        { value: '02', label: 'Tháng 2' },
        { value: '03', label: 'Tháng 3' },
        { value: '04', label: 'Tháng 4' },
        { value: '05', label: 'Tháng 5' },
        { value: '06', label: 'Tháng 6' },
        { value: '07', label: 'Tháng 7' },
        { value: '08', label: 'Tháng 8' },
        { value: '09', label: 'Tháng 9' },
        { value: '10', label: 'Tháng 10' },
        { value: '11', label: 'Tháng 11' },
        { value: '12', label: 'Tháng 12' }
      ];
    }
  };

  // Initialize with current month and year (or from URL params)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const monthParam = urlParams.get('month');
    const yearParam = urlParams.get('year');
    
    if (monthParam && yearParam) {
      setSelectedMonth(monthParam);
      setSelectedYear(yearParam);
    } else {
      const now = new Date();
      setSelectedMonth(String(now.getMonth() + 1).padStart(2, '0'));
      setSelectedYear(String(now.getFullYear()));
    }
  }, []);

  // Initialize summary data for T1-T8 months
  useEffect(() => {
    if (selectedYear === '2025') {
      const initialSummaryData = [
        { month: '01', label: 'Tháng 1', amount: '' },
        { month: '02', label: 'Tháng 2', amount: '' },
        { month: '03', label: 'Tháng 3', amount: '' },
        { month: '04', label: 'Tháng 4', amount: '' },
        { month: '05', label: 'Tháng 5', amount: '' },
        { month: '06', label: 'Tháng 6', amount: '' },
        { month: '07', label: 'Tháng 7', amount: '' },
        { month: '08', label: 'Tháng 8', amount: '' }
      ];
      setSummaryData(initialSummaryData);
    }
  }, [selectedYear]);

  // Check if current selection is for 2025 T1-T8 summary mode
  const isSummaryMode = selectedYear === '2025' && selectedMonth === 'T1-T8';
  const { duocMo, thongBao } = useThangDoanhSo(selectedMonth, selectedYear);

  // Validate input to only allow numbers
  const validateNumericInput = (value) => {
    if (value === '') return '';
    const numericValue = value.replace(/[^\d.,]/g, '');
    return numericValue;
  };

  // Handle summary data change
  const handleSummaryDataChange = (month, value, shouldFormat = false) => {
    let processedValue = value;
    
    if (shouldFormat) {
      const numericValue = parseNumber(value);
      processedValue = numericValue ? formatNumber(numericValue) : '';
    } else {
      processedValue = validateNumericInput(value);
    }
    
    setSummaryData(prevData => 
      prevData.map(item => 
        item.month === month 
          ? { ...item, amount: processedValue }
          : item
      )
    );
  };

  // Helper function to format number with thousands separator (rounded, no decimals)
  const formatNumber = (value) => {
    if (!value || value === '') return '';
    const numericValue = value.replace(/[^\d]/g, '');
    const roundedValue = Math.round(parseFloat(numericValue) || 0);
    return roundedValue.toLocaleString();
  };

  // Helper function to parse formatted number back to numeric value
  const parseNumber = (formattedValue) => {
    if (!formattedValue || formattedValue === '') return '';
    return formattedValue.replace(/[^\d]/g, '');
  };

  // Helper function to get price from priceTable based on quantity
  const getPriceByQuantity = (priceTable, quantity, fallbackPrice) => {
    if (!priceTable || !Array.isArray(priceTable) || priceTable.length === 0) {
      return fallbackPrice || 0;
    }
    const qty = parseInt(quantity) || 1;
    const matchingTier = priceTable.find(tier =>
      qty >= (tier.minQuantity || 0) && qty <= (tier.maxQuantity || Infinity)
    );
    return matchingTier ? matchingTier.price : (fallbackPrice || 0);
  };

  // Load sales report data
  const loadSalesData = useCallback(async (month, year) => {
    if (!accessToken || !month || !year) {
      console.warn('Missing required parameters for loading sales data');
      setInitialLoading(false);
      return;
    }

    // Handle T1-T8 summary mode
    if (month === 'T1-T8') {
      setInitialLoading(true);
      setProducts([]);
      
      try {
        console.log('Loading summary data for T1-T8, year:', year);
        
        const monthPromises = ['01', '02', '03', '04', '05', '06', '07', '08'].map(async (monthNum) => {
          try {
            const salesResult = await CungVeDichAPI.getSalesReport(monthNum, year, accessToken);
            return {
              month: monthNum,
              amount: salesResult.success && salesResult.salesData && salesResult.salesData.length > 0 
                ? parseFloat(salesResult.salesData[0].amount) || 0
                : 0
            };
          } catch (error) {
            console.error(`Error loading data for month ${monthNum}:`, error);
            return { month: monthNum, amount: 0 };
          }
        });

        const monthResults = await Promise.all(monthPromises);
        
        setSummaryData(prevData => 
          prevData.map(item => {
            const loadedMonth = monthResults.find(result => result.month === item.month);
            return {
              ...item,
              amount: loadedMonth ? formatNumber(loadedMonth.amount.toString()) : ''
            };
          })
        );
        
        console.log('Summary data loaded successfully:', monthResults);
      } catch (error) {
        console.error('Error loading summary data:', error);
        setMessage('Có lỗi xảy ra khi tải dữ liệu tổng hợp: ' + error.message);
        setMessageType('error');
      } finally {
        setInitialLoading(false);
      }
      return;
    }

    try {
      setInitialLoading(true);
      console.log('Loading sales report data for month:', month, 'year:', year);
      
      // Load product inventory (for priceTable and full catalog) + sales report in parallel
      const [inventoryResult, salesResult] = await Promise.all([
        CungVeDichAPI.getProductInventory(accessToken),
        CungVeDichAPI.getSalesReport(month, year, accessToken)
      ]);

      // Build inventory map for priceTable lookup
      let inventoryMap = {};
      if (inventoryResult.success && inventoryResult.products) {
        setAllProducts(inventoryResult.products);
        inventoryResult.products.forEach(p => {
          inventoryMap[p.id.toString()] = p;
        });
        console.log('Product inventory loaded:', inventoryResult.products.length, 'products');
      }
      
      if (salesResult.success && salesResult.salesData && salesResult.salesData.length > 0) {
        // Get the latest updated date
        const salesWithUpdatedAt = salesResult.salesData.filter(sale => sale.saleReportUpdatedAt);
        if (salesWithUpdatedAt.length > 0) {
          const latestUpdate = salesWithUpdatedAt.reduce((latest, sale) => {
            const saleDate = new Date(sale.saleReportUpdatedAt);
            const latestDate = new Date(latest);
            return saleDate > latestDate ? sale.saleReportUpdatedAt : latest;
          }, salesWithUpdatedAt[0].saleReportUpdatedAt);
          setLastUpdatedDate(latestUpdate);
        }
        
        // Transform existing sales data, merge with inventory for priceTable
        // Only show products with quantity > 0 or amount > 0
        const transformedProducts = salesResult.salesData
          .filter(sale => {
            const count = parseInt(sale.count) || 0;
            const amount = parseFloat(sale.amount) || 0;
            return count > 0 || amount > 0;
          })
          .map(sale => {
            const inventory = inventoryMap[sale.productId?.toString()];
            return {
              id: sale.productId,
              name: sale.productName,
              code: sale.productCode,
              price: sale.productPrice,
              description: sale.productDescription,
              imageUrl: sale.productImageUrl,
              categoryId: sale.categoryId,
              categoryName: sale.categoryName,
              categoryCode: sale.categoryCode,
              quantity: sale.count?.toString() || '',
              // Store display price as ÷1000 value
              formattedPrice: ((sale.price || 0) / 1000).toString(),
              salesAmount: formatNumber((parseFloat(sale.amount) || 0).toString()),
              notes: sale.saleReportNotes || '',
              // Merge priceTable from inventory
              priceTable: inventory?.priceTable || [],
              fallbackPrice: inventory?.price || sale.productPrice || 0,
            };
          });
        
        // Sort products by name in ascending order
        const sortedProducts = transformedProducts.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setProducts(sortedProducts);
        console.log('Sales report data loaded successfully:', sortedProducts.length, 'products with sales');
      } else {
        // No existing sales data - start with empty list, user adds via dropdown
        console.log('No sales data found for the selected period');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading sales data:', error);
      setMessage('Có lỗi xảy ra khi tải dữ liệu: ' + error.message);
      setMessageType('error');
    } finally {
      setInitialLoading(false);
    }
  }, [accessToken]);

  // Load data on component mount
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      loadSalesData(selectedMonth, selectedYear);
    }
  }, [accessToken, selectedMonth, selectedYear, loadSalesData]);

  // If URL has step=2, jump to proof upload step after data finishes loading
  useEffect(() => {
    if (initialLoading) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('step') !== '2') return;
    if (step !== 1) return; // already transitioned

    // Build snapshot from loaded products so handleSubmitProof has the correct payload
    const details = products
      .filter(p => (parseFloat(p.quantity) || 0) > 0)
      .map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: parseFloat(p.quantity) || 0,
        price: parseFloat(p.formattedPrice) * 1000 || 0,
        amount: parseFloat(parseNumber(p.salesAmount)) || 0,
      }));
    const total = details.reduce((sum, d) => sum + d.amount, 0);
    setSubmittedSnapshot({ totalAmount: total, details });
    setStep(2);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoading]);

  // Helper function to get products with quantity > 0 for confirm dialog
  const getProductsWithQuantity = () => {
    return products.filter(product => {
      const quantity = parseFloat(parseNumber(product.quantity)) || 0;
      return quantity > 0;
    }).map(product => ({
      name: product.name,
      imageUrl: product.imageUrl,
      description: product.description,
      quantity: parseFloat(parseNumber(product.quantity)) || 0,
      price: parseFloat(product.formattedPrice) || 0,
      totalAmount: parseFloat(parseNumber(product.salesAmount)) || 0
    }));
  };

  // Helper function to get summary data for confirm dialog
  const getSummaryDataForConfirm = () => {
    return summaryData.map(item => ({
      month: item.month,
      label: item.label,
      amount: parseFloat(parseNumber(item.amount)) || 0
    }));
  };

  // Handle adding product from dropdown
  const handleProductSelect = (productId) => {
    if (!productId) {
      setSelectedProductId('');
      return;
    }

    const product = allProducts.find(p => p.id.toString() === productId);
    if (!product) return;

    // Check if product already in list
    const existingProduct = products.find(p => p.id === product.id);
    if (existingProduct) {
      setMessage('Sản phẩm đã có trong danh sách');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
      setSelectedProductId('');
      return;
    }

    // Add product to the top of the list with priceTable pricing
    const initialQuantity = 1;
    const initialPrice = getPriceByQuantity(product.priceTable, initialQuantity, product.price);

    const newProduct = {
      id: product.id,
      name: product.name,
      code: product.code,
      price: product.price,
      description: product.description,
      imageUrl: product.imageUrl,
      categoryId: product.categoryId,
      categoryName: product.categoryName,
      categoryCode: product.categoryCode,
      quantity: '1',
      formattedPrice: (initialPrice / 1000).toString(),
      salesAmount: formatNumber(initialPrice.toString()),
      notes: '',
      priceTable: product.priceTable || [],
      fallbackPrice: product.price || 0,
    };

    setProducts([newProduct, ...products]);
    setSelectedProductId('');
  };

  // Handle removing product from list
  const handleRemoveProduct = (productId) => {
    setProducts(products.filter(p => p.id !== productId));
  };

  const handleFieldChange = (productId, field, value, shouldFormat = false) => {
    setProducts(prevProducts => 
      prevProducts.map(product => {
        if (product.id === productId) {
          // Validate input for price fields to only allow numbers
          let validatedValue = value;
          if (field === 'formattedPrice' && !shouldFormat) {
            validatedValue = validateNumericInput(value);
          }
          
          const updatedProduct = { ...product, [field]: validatedValue };
          
          // Auto-calculate when quantity changes
          if (field === 'quantity') {
            const qty = parseInt(validatedValue) || 0;
            // Lookup priceTable for suggested price when quantity changes
            if (product.priceTable && product.priceTable.length > 0) {
              const suggestedPrice = getPriceByQuantity(product.priceTable, qty, product.fallbackPrice);
              updatedProduct.formattedPrice = (suggestedPrice / 1000).toString();
            }
            // Recalculate sales amount
            const price = parseFloat(updatedProduct.formattedPrice) || 0;
            const salesAmount = Math.round(qty * price * 1000);
            updatedProduct.salesAmount = formatNumber(salesAmount.toString());
          }
          
          // Auto-calculate when price changes
          if (field === 'formattedPrice') {
            const quantity = parseFloat(parseNumber(product.quantity)) || 0;
            const price = parseFloat(parseNumber(validatedValue)) || 0;
            const salesAmount = Math.round(quantity * price * 1000);
            updatedProduct.salesAmount = formatNumber(salesAmount.toString());
          }
          
          // Handle direct sales amount changes
          if (field === 'salesAmount') {
            updatedProduct.salesAmount = value;
          }
          
          return updatedProduct;
        }
        return product;
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowConfirmDialog(true);
  };

  const handleSubmitConfirm = async () => {
    setLoading(true);
    setMessage('');

    try {
      if (!selectedMonth || !selectedYear) {
        setMessage('Vui lòng chọn tháng và năm');
        setMessageType('error');
        setLoading(false);
        return;
      }

      let salesReportData;

      if (isSummaryMode) {
        // Validate summary data
        const hasEmptySummaryFields = summaryData.some(item => {
          const numericAmount = parseFloat(parseNumber(item.amount));
          return isNaN(numericAmount) || item.amount.trim() === '';
        });
        
        if (hasEmptySummaryFields) {
          setMessage('Vui lòng nhập đầy đủ thành tiền cho tất cả các tháng (có thể nhập 0)');
          setMessageType('error');
          setLoading(false);
          return;
        }

        // Submit each month as separate report
        const promises = summaryData.map(async (item) => {
          const numericAmount = parseFloat(parseNumber(item.amount)) || 0;
          
          const monthReportData = {
            month: parseInt(item.month),
            year: parseInt(selectedYear),
            saleReports: [{
              productId: null,
              count: 1,
              price: 0,
              amount: numericAmount,
              notes: `Tổng doanh số tháng ${item.month}/${selectedYear}`
            }]
          };
          
          return CungVeDichAPI.submitSalesReport(monthReportData, accessToken);
        });

        const results = await Promise.all(promises);
        const allSuccess = results.every(result => result.success);

        if (allSuccess) {
          const total = summaryData.reduce((sum, item) => sum + (parseFloat(parseNumber(item.amount)) || 0), 0);
          const details = summaryData.map(item => ({
            month: item.month,
            label: item.label,
            amount: parseFloat(parseNumber(item.amount)) || 0,
          }));
          setSubmittedSnapshot({ totalAmount: total, details });
          setMessage('');
          setStep(2);
        } else {
          throw new Error('Một số báo cáo không được lưu thành công');
        }
        
      } else {
        // Normal mode - send selected products
        salesReportData = {
          month: parseInt(selectedMonth),
          year: parseInt(selectedYear),
          saleReports: products.map(product => ({
            productId: product.id,
            count: parseInt(parseNumber(product.quantity)) || 0,
            price: (parseFloat(product.formattedPrice) || 0) * 1000,
            amount: parseFloat(parseNumber(product.salesAmount)) || 0,
            notes: product.notes || `Bán tháng ${selectedMonth}/${selectedYear}`
          }))
        };

        console.log('Sales report data to submit:', salesReportData);

        const result = await CungVeDichAPI.submitSalesReport(salesReportData, accessToken);
        
        if (result.success) {
          // Build snapshot for approval submission
          const total = products.reduce((sum, p) => sum + (parseFloat(parseNumber(p.salesAmount)) || 0), 0);
          const details = products
            .filter(p => (parseFloat(parseNumber(p.quantity)) || 0) > 0)
            .map(p => ({
              productId: p.id,
              productName: p.name,
              productCode: p.code,
              quantity: parseInt(parseNumber(p.quantity)) || 0,
              price: (parseFloat(p.formattedPrice) || 0) * 1000,
              amount: parseFloat(parseNumber(p.salesAmount)) || 0,
            }));
          setSubmittedSnapshot({ totalAmount: total, details });
          setMessage('');
          setStep(2);
        } else {
          throw new Error(result.message || 'Lưu báo cáo thất bại');
        }
      }
      
    } catch (error) {
      console.error('Error submitting sales report:', error);
      setMessage('Có lỗi xảy ra khi lưu báo cáo doanh số. Vui lòng thử lại.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Handle month/year change
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
    setMessage('');
  };

  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
    setMessage('');
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProofImageFile(file);
    setProofImagePreview(URL.createObjectURL(file));
  };

  const handleSubmitProof = async () => {
    if (!proofImageFile) {
      alert('Vui lòng chọn hình ảnh bằng chứng doanh số');
      return;
    }
    setSubmissionLoading(true);
    try {
      const uploadResult = await uploadImage(accessToken, proofImageFile);
      if (!uploadResult.success) throw new Error(uploadResult.error || 'Upload ảnh thất bại');

      const month = isSummaryMode ? 1 : parseInt(selectedMonth);
      const year = parseInt(selectedYear);
      const payload = {
        month,
        year,
        totalAmount: submittedSnapshot.totalAmount,
        details: submittedSnapshot.details,
        proofImage: uploadResult.imageUrl,
      };
      const result = await SaleReportSubmissionAPI.submit(accessToken, payload);
      if (!result.success) throw new Error(result.error || 'Nộp bằng chứng thất bại');

      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const params = new URLSearchParams();
      if (token) params.append('token', token);
      params.append('month', String(month).padStart(2, '0'));
      params.append('year', String(year));
      navigate(`/doanh-so?${params.toString()}`);
    } catch (err) {
      alert('Lỗi: ' + err.message);
    } finally {
      setSubmissionLoading(false);
    }
  };

  const handleImageClick = (imageUrl, productName, productDescription) => {
    setSelectedImage({ url: imageUrl, name: productName, description: productDescription });
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SystemHeader 
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Chỉnh sửa Doanh số"
        pageSubtitle="Nhập/Chỉnh sửa báo cáo doanh số theo tháng"
        onRefreshPoints={onRefreshPoints}
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-4 lg:px-6">
        {/* Back button */}
        <div className="py-2">
          <button onClick={() => navigate('/doanh-so')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ color: '#2563eb', background: '#2563eb12' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            <span>Quay lại</span>
          </button>
        </div>

        {/* ── STEP 2: Proof upload ── */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-800">Bước 2: Nộp bằng chứng để duyệt</h2>
            </div>
            <p className="text-sm text-gray-500 mb-1">
              Doanh số tháng {selectedMonth === 'T1-T8' ? 'T1–T8' : `${selectedMonth}`}/{selectedYear} đã lưu thành công.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Tổng: <span className="font-semibold text-gray-800">{submittedSnapshot.totalAmount.toLocaleString()}đ</span>
            </p>
            <p className="text-sm text-gray-700 font-medium mb-2">Upload ảnh bạn đã gửi cấp trên bảng chốt đơn kèm xác nhận của cấp trên:</p>
            <label className="block w-full cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors mb-4">
              <input type="file" accept="image/*" className="hidden" onChange={handleProofFileChange} />
              {proofImagePreview ? (
                <img src={proofImagePreview} alt="proof" className="max-h-48 mx-auto rounded object-contain" />
              ) : (
                <div className="text-gray-400 text-sm">
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Nhấn để chọn ảnh
                </div>
              )}
            </label>
            <button
              onClick={handleSubmitProof}
              disabled={!proofImageFile || submissionLoading}
              className="w-full py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: '#FF5722' }}
            >
              {submissionLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Đang nộp...
                </span>
              ) : 'Nộp để duyệt'}
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams();
                params.append('month', String(isSummaryMode ? '01' : selectedMonth).padStart(2, '0'));
                params.append('year', selectedYear);
                navigate(`/doanh-so?${params.toString()}`);
              }}
              className="w-full mt-2 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Bỏ qua, nộp sau
            </button>
          </div>
        )}

        {/* ── STEP 1: Form ── */}
        {step === 1 && (
        <div className="bg-white rounded-lg shadow-sm border flex flex-col h-[calc(100vh-200px)]">

          <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            {/* Message Display */}
            {message && (
              <div className={`mx-6 mt-6 p-4 rounded-lg flex-shrink-0 ${
                messageType === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {message}
              </div>
            )}

            {/* Month/Year Selector - Compact */}
            <div className="py-2 border-b border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Chọn tháng</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => handleMonthChange(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Chọn tháng</option>
                    {getMonthOptions(selectedYear).map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Chọn năm</label>
                  <input
                    type="number"
                    value={selectedYear}
                    onChange={(e) => handleYearChange(e.target.value)}
                    min="2020"
                    max="2030"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập năm"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Summary Statistics - Compact */}
            <div className="py-2 border-b border-gray-200 flex-shrink-0 bg-blue-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!isSummaryMode ? (
                  <>
                    <div className="bg-white rounded p-2 border border-blue-200 hidden sm:block">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mr-1">
                            <svg className="w-2 h-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-gray-500">Tổng số lượng</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {products.reduce((total, product) => {
                            const quantity = parseFloat(parseNumber(product.quantity)) || 0;
                            return total + quantity;
                          }, 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="bg-white rounded p-2 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-1">
                            <svg className="w-2 h-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-gray-500">Doanh số</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {(() => {
                            const totalAmount = products.reduce((total, product) => {
                              const amount = parseFloat(parseNumber(product.salesAmount)) || 0;
                              return total + amount;
                            }, 0);
                            return formatNumber(totalAmount.toString());
                          })()}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded p-2 border border-green-200 col-span-1 sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-1">
                          <svg className="w-2 h-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-gray-500">Tổng doanh số T1-T8</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">
                        {(() => {
                          const totalAmount = summaryData.reduce((total, item) => {
                            const amount = parseFloat(parseNumber(item.amount)) || 0;
                            return total + amount;
                          }, 0);
                          return formatNumber(totalAmount.toString());
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Selector - Only for normal mode */}
            {!isSummaryMode && (
              <div className="py-2 px-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Thêm sản phẩm
                  </label>
                  <AutocompleteSelect
                    options={allProducts}
                    value={selectedProductId}
                    onChange={handleProductSelect}
                    placeholder="Tìm kiếm sản phẩm..."
                    displayField={(product) => `${product.name}`}
                    searchFields={['name', 'code']}
                    disabled={initialLoading}
                    maxResults={100}
                  />
                </div>
              </div>
            )}

            {/* Products List or Summary Form */}
            <div className="flex-1 overflow-y-auto">
              {isSummaryMode ? (
                // Summary Mode - Show monthly total input form
                <div className="p-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-medium text-blue-800 mb-2">Nhập tổng doanh số theo tháng</h3>
                    <p className="text-xs text-blue-600">
                      Nhập tổng thành tiền cho từng tháng T1-T8. Mỗi tháng sẽ được lưu như một bản ghi riêng biệt.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {summaryData.map((item) => (
                      <div key={item.month} className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {item.label}
                        </label>
                        <input
                          type="text"
                          value={item.amount}
                          onChange={(e) => handleSummaryDataChange(item.month, e.target.value)}
                          onBlur={(e) => {
                            handleSummaryDataChange(item.month, e.target.value, true);
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập thành tiền (VD: 32000000 hoặc 32.000.000)"
                          inputMode="numeric"
                          required
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Normal Mode - Show selected products table
                <>
                  {initialLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-3 text-gray-600">Đang tải dữ liệu...</span>
                    </div>
                  ) : products.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Chưa có sản phẩm nào. Hãy chọn sản phẩm từ dropdown phía trên.</p>
                    </div>
                  ) : (
                    <div className="overflow-y-auto max-h-[calc(100vh-450px)] sm:max-h-[calc(100vh-400px)]">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300">
                                Sản phẩm
                              </th>
                              <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-20">
                                SL
                              </th>
                              <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-24">
                                <div>Đơn giá</div>
                                <div className="text-[10px] font-normal normal-case text-gray-500">(x1.000)</div>
                              </th>
                              <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-24">
                                Thành tiền
                              </th>
                              <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-12">
                                Xóa
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {products.map((product) => {
                              const displayPrice = product.formattedPrice || '';

                              return (
                                <tr key={product.id} className="hover:bg-gray-50">
                                  {/* Product Name */}
                                  <td className="px-2 py-1.5">
                                    <div className="flex items-center space-x-2">
                                      {product.imageUrl && (
                                        <div 
                                          className="w-8 h-8 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                          onClick={() => handleImageClick(product.imageUrl, product.name, product.description)}
                                        >
                                          <img 
                                            src={product.imageUrl} 
                                            alt={product.name}
                                            className="w-full h-full object-cover rounded border border-gray-200"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        </div>
                                      )}
                                      <div className="text-xs font-medium text-gray-900">
                                        {product.name}
                                      </div>
                                    </div>
                                  </td>

                                  {/* Quantity Input */}
                                  <td className="px-1 py-1.5">
                                    <input
                                      type="number"
                                      min="0"
                                      value={product.quantity}
                                      style={{ width: '55px', minHeight: '28px' }}
                                      onChange={(e) => handleFieldChange(product.id, 'quantity', e.target.value)}
                                      className="px-1.5 py-0.5 text-base md:text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                                      placeholder="0"
                                    />
                                  </td>

                                  {/* Price Input with .000 suffix */}
                                  <td className="px-1 py-1.5">
                                    <div className="flex items-center">
                                      <input
                                        type="number"
                                        min="0"
                                        value={displayPrice}
                                        style={{ width: '55px', minHeight: '28px' }}
                                        onChange={(e) => handleFieldChange(product.id, 'formattedPrice', e.target.value)}
                                        className="px-1 py-0.5 text-base md:text-xs border border-gray-300 rounded-l focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                                        placeholder="0"
                                      />
                                      <span className="px-1 py-0.5 text-base md:text-xs bg-gray-100 border border-l-0 border-gray-300 rounded-r text-gray-500 select-none" style={{ minHeight: '28px', display: 'flex', alignItems: 'center' }}>.000</span>
                                    </div>
                                  </td>

                                  {/* Sales Amount (readonly, auto-calculated) */}
                                  <td className="px-1 py-1.5">
                                    <div className="px-1.5 py-0.5 text-xs bg-blue-50 border border-blue-200 rounded text-center font-semibold text-blue-700" style={{ minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {product.salesAmount}₫
                                    </div>
                                  </td>

                                  {/* Remove Button */}
                                  <td className="px-1 py-1.5 text-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveProduct(product.id)}
                                      className="text-red-600 hover:text-red-800 focus:outline-none"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {!duocMo && <ThongBaoKhoaThang month={selectedMonth} year={selectedYear} thongBao={thongBao} />}
            {/* Action Buttons - Fixed at bottom — ẩn khi tháng chưa mở cập nhật (vào thẳng URL cũng không lưu được) */}
            {duocMo && <div className="flex-shrink-0 border-t border-gray-200 bg-white py-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Đang xử lý...
                    </div>
                  ) : (
                    isSummaryMode ? 'Lưu Báo cáo Tổng hợp' : 'Lưu đơn chốt tạm thời'
                  )}
                </button>
              </div>
            </div>}
          </form>
        </div>
        )}
      </div>

      {/* Confirm Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-6">
          <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg p-4 w-full max-h-[90vh] overflow-y-auto">
            <h1 className="text-lg font-bold text-center mb-4 text-gray-800">
              {userInfo?.id || 'N/A'}-{userInfo?.name || 'N/A'} ( DS {selectedMonth === 'T1-T8' ? 'T1-T8' : `T${selectedMonth}`}/{selectedYear})
            </h1>

            {/* Bảng báo cáo chi tiết */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="pb-2 font-semibold text-left text-gray-600">Sản phẩm</th>
                  <th className="pb-2 px-1 font-semibold text-center text-gray-600">Chi tiết</th>
                  <th className="pb-2 font-semibold text-right text-gray-600">Thành tiền</th>
                </tr>
              </thead>
              <tbody>
                {isSummaryMode ? (
                  // Summary Mode - Show monthly data
                  getSummaryDataForConfirm().map((item, index) => (
                    <tr key={index} className="border-b border-gray-200 last:border-b-0">
                      <td className="py-1.5 pr-1 align-top">{item.label}</td>
                      <td className="py-1.5 px-1 text-center align-top text-gray-600">Tổng doanh số</td>
                      <td className="py-1.5 pl-1 text-right font-medium align-top">
                        {formatNumber(item.amount.toString())}đ
                      </td>
                    </tr>
                  ))
                ) : (
                  // Normal Mode - Show products with quantity > 0
                  getProductsWithQuantity().length > 0 ? (
                    getProductsWithQuantity().map((product, index) => (
                      <tr key={index} className="border-b border-gray-200 last:border-b-0">
                        <td className="py-1.5 pr-1 align-top">
                          <div className="flex items-center space-x-2">
                            {product.imageUrl && (
                              <div 
                                className="w-8 h-8 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(product.imageUrl, product.name, product.description)}
                              >
                                <img 
                                  src={product.imageUrl} 
                                  alt={product.name}
                                  className="w-full h-full object-cover rounded border border-gray-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            <span className="text-sm">{product.name}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-1 text-center align-top text-gray-600">
                          {product.quantity} × {formatNumber(product.price.toString())}
                        </td>
                        <td className="py-1.5 pl-1 text-right font-medium align-top">
                          {formatNumber(product.totalAmount.toString())}đ
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-b border-gray-200">
                      <td colSpan="3" className="py-4 text-center text-gray-500">
                        Không có sản phẩm nào có số lượng &gt; 0
                      </td>
                    </tr>
                  )
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td colSpan="2" className="pt-2 font-bold text-left text-base text-gray-800">Tổng cộng</td>
                  <td className="pt-2 font-bold text-right text-base text-gray-800">
                    {(() => {
                      let totalAmount = 0;
                      if (isSummaryMode) {
                        totalAmount = getSummaryDataForConfirm().reduce((total, item) => total + item.amount, 0);
                      } else {
                        totalAmount = getProductsWithQuantity().reduce((total, product) => total + product.totalAmount, 0);
                      }
                      return formatNumber(totalAmount.toString()) + 'đ';
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>

            {/* Lưu ý quan trọng */}
            <div className="mt-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-xs text-red-700">
                  <span className="font-medium">Lưu ý:</span> Phải chụp màn hình này gửi cho cấp trên nhờ cấp trên duyệt hoặc xác nhận.
                </div>
              </div>
            </div>

            {/* Ngày cập nhật doanh số gần nhất */}
            {!isSummaryMode && lastUpdatedDate && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="text-xs text-green-700">
                    <span className="font-medium">Ngày cập nhật doanh số gần nhất:</span> {new Date(lastUpdatedDate).toLocaleDateString('vi-VN', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowConfirmDialog(false);
                  await handleSubmitConfirm();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Xác nhận lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{selectedImage.name}</h3>
                <button
                  onClick={closeImageModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name}
                className="w-full h-auto max-h-[60vh] object-contain rounded-lg mb-4"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                }}
              />
              {selectedImage.description && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Mô tả sản phẩm:</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {selectedImage.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleReportForm;
